import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

import Paper from 'components/Paper';
import Table from 'components/Table';
import { Row } from 'styles/components';

import AccountsPayableActions from 'store/ducks/accountsPayable';
import AccountsReceivableActions from 'store/ducks/accountsReceivable';

import {
  Container,
  KpiGrid,
  KpiCard,
  KpiLabel,
  KpiValue,
  KpiHint,
  SplitGrid,
  SectionCard,
  SectionTitleRow,
  SectionTitle,
  TableContainer,
  StatusPill,
  LinkButton,
  FilterBar,
  FilterLabel,
  FilterSelect,
  FilterDateInput,
  FilterHint,
} from './styles';

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function safeDate(dateLike) {
  if (!dateLike) return null;
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toPlainArray(maybeImmutable) {
  if (!maybeImmutable) return [];
  // seamless-immutable arrays have asMutable
  if (typeof maybeImmutable.asMutable === 'function') {
    try {
      const mutable = maybeImmutable.asMutable({ deep: true });
      return Array.isArray(mutable) ? mutable : [];
    } catch (_) {
      // fallback below
    }
  }
  if (Array.isArray(maybeImmutable)) return [...maybeImmutable];
  try {
    return Array.from(maybeImmutable);
  } catch (_) {
    return [];
  }
}

function statusLabelReceber(status) {
  const map = {
    pending: { label: 'Aberto', color: '#F27160' },
    received: { label: 'Recebido', color: '#44DD30' },
    overdue: { label: 'Vencido', color: '#F27160' },
  };
  return map[status] || map.pending;
}

function statusLabelPagar(status) {
  const map = {
    pending: { label: 'Aberto', color: '#F27160' },
    paid: { label: 'Pago', color: '#44DD30' },
    overdue: { label: 'Vencido', color: '#F27160' },
  };
  return map[status] || map.pending;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function addDays(d, days) {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}

function periodRange(preset, customFrom, customTo) {
  const today = startOfDay(new Date());
  if (preset === 'all') return { from: startOfDay(new Date('1970-01-01')), to: endOfDay(new Date('2999-12-31')) };
  if (preset === 'today') return { from: today, to: endOfDay(today) };
  if (preset === '7d') return { from: today, to: endOfDay(addDays(today, 6)) };
  if (preset === '30d') return { from: today, to: endOfDay(addDays(today, 29)) };
  if (preset === 'month') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: startOfDay(from), to: endOfDay(to) };
  }
  if (preset === 'year') {
    const from = new Date(today.getFullYear(), 0, 1);
    const to = new Date(today.getFullYear(), 11, 31);
    return { from: startOfDay(from), to: endOfDay(to) };
  }
  // custom
  const from = customFrom ? startOfDay(new Date(customFrom)) : today;
  const to = customTo ? endOfDay(new Date(customTo)) : endOfDay(today);
  return { from, to };
}

function inRangeByDueDate(item, from, to) {
  const due = safeDate(item?.dueDate);
  // Se não tiver vencimento, não filtrar (mantém no dashboard)
  if (!due) return true;
  const t = due.getTime();
  return t >= from.getTime() && t <= to.getTime();
}

export default function FinanceiroDashboard() {
  const dispatch = useDispatch();
  const history = useHistory();

  const { accounts: contasPagar, isLoading: loadingPagar } = useSelector((state) => state.accountsPayable);
  const { accounts: contasReceber, isLoading: loadingReceber } = useSelector((state) => state.accountsReceivable);

  const [periodPreset, setPeriodPreset] = useState('all'); // default: todos
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const { from: rangeFrom, to: rangeTo } = useMemo(
    () => periodRange(periodPreset, customFrom, customTo),
    [periodPreset, customFrom, customTo]
  );

  useEffect(() => {
    dispatch(AccountsPayableActions.getAccountsPayableRequest());
    dispatch(AccountsReceivableActions.getAccountsReceivableRequest());
  }, [dispatch]);

  const { totalReceberAberto, totalPagarAberto, fluxoProjetado } = useMemo(() => {
    const pagarAberto = toPlainArray(contasPagar)
      .filter((c) => c?.status !== 'paid')
      .filter((c) => inRangeByDueDate(c, rangeFrom, rangeTo));

    const receberAberto = toPlainArray(contasReceber)
      .filter((c) => c?.status !== 'received')
      .filter((c) => inRangeByDueDate(c, rangeFrom, rangeTo));

    const totalPagar = pagarAberto.reduce((sum, c) => sum + Number(c.value || 0), 0);
    const totalReceber = receberAberto.reduce((sum, c) => sum + Number(c.value || 0), 0);

    return {
      totalReceberAberto: totalReceber,
      totalPagarAberto: totalPagar,
      fluxoProjetado: totalReceber - totalPagar,
    };
  }, [contasPagar, contasReceber, rangeFrom, rangeTo]);

  const topReceber = useMemo(() => {
    const arr = toPlainArray(contasReceber)
      .filter((c) => c && c.status !== 'received')
      .filter((c) => inRangeByDueDate(c, rangeFrom, rangeTo))
      .sort((a, b) => {
        const da = safeDate(a.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const db = safeDate(b.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        return da - db;
      })
      .slice(0, 3);
    return arr;
  }, [contasReceber, rangeFrom, rangeTo]);

  const topPagar = useMemo(() => {
    const arr = toPlainArray(contasPagar)
      .filter((c) => c && c.status !== 'paid')
      .filter((c) => inRangeByDueDate(c, rangeFrom, rangeTo))
      .sort((a, b) => {
        const da = safeDate(a.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const db = safeDate(b.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        return da - db;
      })
      .slice(0, 3);
    return arr;
  }, [contasPagar, rangeFrom, rangeTo]);

  const rangeLabel = useMemo(() => {
    try {
      const a = format(rangeFrom, 'dd/MM/yyyy');
      const b = format(rangeTo, 'dd/MM/yyyy');
      return `${a} - ${b}`;
    } catch (_) {
      return '';
    }
  }, [rangeFrom, rangeTo]);

  const tableReceberData = topReceber.map((c) => {
    const due = safeDate(c.dueDate);
    const statusInfo = statusLabelReceber(c.status);
    return {
      id: c.id,
      description: c.description || '-',
      value: formatCurrency(c.value),
      dueDate: due ? format(due, 'dd/MM/yyyy') : '-',
      customer: c.customer || '-',
      formaPagamento: c.formaPagamento || '-',
      status: <StatusPill color={statusInfo.color}>{statusInfo.label}</StatusPill>,
    };
  });

  const tablePagarData = topPagar.map((c) => {
    const due = safeDate(c.dueDate);
    const statusInfo = statusLabelPagar(c.status);
    return {
      id: c.id,
      description: c.description || '-',
      value: formatCurrency(c.value),
      dueDate: due ? format(due, 'dd/MM/yyyy') : '-',
      supplier: c.supplier || '-',
      formaPagamento: c.formaPagamento || '-',
      status: <StatusPill color={statusInfo.color}>{statusInfo.label}</StatusPill>,
    };
  });

  return (
    <Container>
      <Paper
        title="DASHBOARD FINANCEIRO"
        rightContent={
          <Row>
            <FilterBar>
              <FilterLabel>Período:</FilterLabel>
              <FilterSelect value={periodPreset} onChange={(e) => setPeriodPreset(e.target.value)}>
                <option value="all">Todos</option>
                <option value="today">Hoje</option>
                <option value="7d">Próximos 7 dias</option>
                <option value="30d">Próximos 30 dias</option>
                <option value="month">Mês atual</option>
                <option value="year">Ano atual</option>
                <option value="custom">Personalizado</option>
              </FilterSelect>
              {periodPreset === 'custom' && (
                <>
                  <FilterDateInput type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                  <FilterDateInput type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                </>
              )}
              <FilterHint>{rangeLabel}</FilterHint>
            </FilterBar>
            <LinkButton onClick={() => history.push('/financeiro/contas-receber')}>Ver Contas a Receber</LinkButton>
            <LinkButton onClick={() => history.push('/financeiro/contas-pagar')}>Ver Contas a Pagar</LinkButton>
          </Row>
        }
      >
        <KpiGrid>
          <KpiCard accent="#44DD30">
            <KpiLabel>Total a Receber (Aberto)</KpiLabel>
            <KpiValue color="#44DD30">{formatCurrency(totalReceberAberto)}</KpiValue>
            <KpiHint>{loadingReceber ? 'Carregando...' : 'Somente títulos não recebidos'}</KpiHint>
          </KpiCard>

          <KpiCard accent="#F27160">
            <KpiLabel>Total a Pagar (Aberto)</KpiLabel>
            <KpiValue color="#F27160">{formatCurrency(totalPagarAberto)}</KpiValue>
            <KpiHint>{loadingPagar ? 'Carregando...' : 'Somente títulos não pagos'}</KpiHint>
          </KpiCard>

          <KpiCard accent="#122d58">
            <KpiLabel>Fluxo de Caixa Projetado</KpiLabel>
            <KpiValue color="#122d58">{formatCurrency(fluxoProjetado)}</KpiValue>
            <KpiHint>Receber (aberto) - Pagar (aberto)</KpiHint>
          </KpiCard>

          <KpiCard accent="#f2c94c">
            <KpiLabel>Saldo Atual Bancário</KpiLabel>
            <KpiValue color="#333">{formatCurrency(0)}</KpiValue>
            <KpiHint>Não configurado</KpiHint>
          </KpiCard>
        </KpiGrid>

        <SplitGrid>
          <SectionCard>
            <SectionTitleRow>
              <SectionTitle>Contas a Receber (próximos vencimentos)</SectionTitle>
              <LinkButton onClick={() => history.push('/financeiro/contas-receber')}>Ver todas →</LinkButton>
            </SectionTitleRow>
            <TableContainer>
              <Table
                columns={[
                  { label: 'Descrição', id: 'description' },
                  { label: 'Valor', id: 'value' },
                  { label: 'Vencimento', id: 'dueDate' },
                  { label: 'Cliente', id: 'customer' },
                  { label: 'Forma', id: 'formaPagamento' },
                  { label: 'Status', id: 'status' },
                ]}
                activePage={0}
                totalPages={1}
                data={tableReceberData}
              />
            </TableContainer>
          </SectionCard>

          <SectionCard>
            <SectionTitleRow>
              <SectionTitle>Contas a Pagar (próximos vencimentos)</SectionTitle>
              <LinkButton onClick={() => history.push('/financeiro/contas-pagar')}>Ver todas →</LinkButton>
            </SectionTitleRow>
            <TableContainer>
              <Table
                columns={[
                  { label: 'Descrição', id: 'description' },
                  { label: 'Valor', id: 'value' },
                  { label: 'Vencimento', id: 'dueDate' },
                  { label: 'Fornecedor', id: 'supplier' },
                  { label: 'Forma', id: 'formaPagamento' },
                  { label: 'Status', id: 'status' },
                ]}
                activePage={0}
                totalPages={1}
                data={tablePagarData}
              />
            </TableContainer>
          </SectionCard>
        </SplitGrid>
      </Paper>
    </Container>
  );
}